import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, CreditCard, Trash2, Loader2, Lock, Building2, Mail, Calendar, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";

const PLAN_LABELS = { starter: "Starter · 49€/mois", pro: "Pro · 149€/mois", business: "Business · 399€/mois" };

const STATUS_LABELS = {
  active: { text: "Actif", color: "bg-emerald-100 text-emerald-800" },
  trialing: { text: "Essai", color: "bg-amber-100 text-amber-800" },
  trial: { text: "Essai", color: "bg-amber-100 text-amber-800" },
  past_due: { text: "Paiement en attente", color: "bg-red-100 text-red-800" },
  canceled: { text: "Résilié", color: "bg-zinc-100 text-zinc-700" },
  cancelled: { text: "Résilié", color: "bg-zinc-100 text-zinc-700" },
  refunded: { text: "Remboursé", color: "bg-zinc-100 text-zinc-700" },
  unpaid: { text: "Impayé", color: "bg-red-100 text-red-800" },
};

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  // Profile form
  const [profile, setProfile] = useState({ full_name: "", email: "", company_name: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  // Portal
  const [openingPortal, setOpeningPortal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");

  useEffect(() => {
    document.title = "Paramètres · FlowMind AI";
    fetchDetails();
  }, []);

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name || "",
        email: user.email || "",
        company_name: user.company_name || "",
      });
    }
  }, [user]);

  const fetchDetails = async () => {
    setLoadingDetails(true);
    try {
      const { data } = await api.get("/users/me/full");
      setDetails(data);
    } catch (e) {
      toast.error("Impossible de charger les détails du compte");
    } finally {
      setLoadingDetails(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/users/me", profile);
      toast.success("Profil mis à jour");
      await refresh();
      await fetchDetails();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error("8 caractères minimum");
      return;
    }
    setSavingPw(true);
    try {
      await api.put("/users/me/password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Mot de passe mis à jour");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingPw(false);
    }
  };

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: `${window.location.origin}/app/settings` });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail indisponible");
      setOpeningPortal(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmDelete !== "SUPPRIMER") {
      toast.error('Tapez exactement "SUPPRIMER" pour confirmer');
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/users/me");
      toast.success("Compte supprimé");
      logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setDeleting(false);
    }
  };

  const subscription = details?.subscription;
  const status = user?.subscription_status;
  const statusLabel = STATUS_LABELS[status] || { text: status || "—", color: "bg-zinc-100 text-zinc-700" };
  const renewalDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="settings-page">
        <div className="mb-10">
          <span className="overline">Mon compte</span>
          <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">Paramètres</h1>
        </div>

        {/* ===== Subscription ===== */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={18} className="text-zinc-500" />
            <h2 className="font-display text-xl font-medium tracking-tight">Mon abonnement</h2>
          </div>
          <div className="border border-zinc-200 rounded-sm p-6 bg-white">
            {loadingDetails ? (
              <div className="flex items-center gap-2 text-zinc-500"><Loader2 size={14} className="animate-spin" /> Chargement…</div>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <p className="text-xs text-zinc-500 font-mono tracking-widest uppercase mb-1">Plan actuel</p>
                    <p className="font-display text-2xl font-medium tracking-tight">{PLAN_LABELS[user?.plan] || user?.plan || "—"}</p>
                    <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-sm ${statusLabel.color}`} data-testid="settings-status-badge">
                      {statusLabel.text}
                    </span>
                  </div>
                  {renewalDate && (
                    <div className="text-right">
                      <p className="text-xs text-zinc-500 font-mono tracking-widest uppercase mb-1">Prochain renouvellement</p>
                      <p className="font-display text-lg tracking-tight flex items-center gap-2">
                        <Calendar size={14} /> {renewalDate}
                      </p>
                      {subscription?.cancel_at_period_end && (
                        <p className="text-xs text-amber-700 mt-1">Résiliation programmée à cette date</p>
                      )}
                    </div>
                  )}
                </div>

                {subscription?.card_brand && (
                  <div className="bg-zinc-50 rounded-sm p-3 mb-6 text-sm flex items-center gap-2">
                    <CreditCard size={14} className="text-zinc-500" />
                    <span className="capitalize">{subscription.card_brand}</span>
                    <span className="text-zinc-500">•••• {subscription.card_last4}</span>
                  </div>
                )}

                <Button
                  onClick={openPortal}
                  disabled={openingPortal || !user?.stripe_customer_id}
                  className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11"
                  data-testid="settings-open-portal"
                >
                  {openingPortal ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <ExternalLink size={14} className="mr-2" />
                  )}
                  Gérer mon abonnement (Stripe)
                </Button>
                <p className="text-xs text-zinc-500 mt-2">
                  Annulation, changement de plan, mise à jour CB, téléchargement des factures — tout est géré dans le portail Stripe sécurisé.
                </p>
                {!user?.stripe_customer_id && (
                  <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                    <AlertTriangle size={12} /> Pas encore d'abonnement actif. Choisissez un plan depuis la <a href="/#pricing" className="underline">page d'accueil</a>.
                  </p>
                )}
              </>
            )}
          </div>
        </section>

        {/* ===== Profile ===== */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <User size={18} className="text-zinc-500" />
            <h2 className="font-display text-xl font-medium tracking-tight">Informations du compte</h2>
          </div>
          <form onSubmit={saveProfile} className="border border-zinc-200 rounded-sm p-6 bg-white space-y-4">
            <div>
              <Label htmlFor="full_name" className="text-xs font-mono tracking-widest uppercase">Nom complet</Label>
              <Input id="full_name" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="mt-2 rounded-sm" data-testid="settings-full-name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-mono tracking-widest uppercase">Email</Label>
              <Input id="email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-2 rounded-sm" data-testid="settings-email" />
            </div>
            <div>
              <Label htmlFor="company" className="text-xs font-mono tracking-widest uppercase">Société</Label>
              <Input id="company" value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} className="mt-2 rounded-sm" data-testid="settings-company" />
            </div>
            <Button type="submit" disabled={savingProfile} className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="settings-save-profile">
              {savingProfile && <Loader2 size={14} className="mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </form>
        </section>

        {/* ===== Password ===== */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={18} className="text-zinc-500" />
            <h2 className="font-display text-xl font-medium tracking-tight">Mot de passe</h2>
          </div>
          <form onSubmit={changePassword} className="border border-zinc-200 rounded-sm p-6 bg-white space-y-4">
            <div>
              <Label className="text-xs font-mono tracking-widest uppercase">Mot de passe actuel</Label>
              <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-2 rounded-sm" data-testid="settings-current-pw" />
            </div>
            <div>
              <Label className="text-xs font-mono tracking-widest uppercase">Nouveau mot de passe</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-2 rounded-sm" data-testid="settings-new-pw" />
            </div>
            <div>
              <Label className="text-xs font-mono tracking-widest uppercase">Confirmer</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-2 rounded-sm" data-testid="settings-confirm-pw" />
            </div>
            <Button type="submit" disabled={savingPw || !pw.current_password || !pw.new_password} className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="settings-change-pw">
              {savingPw && <Loader2 size={14} className="mr-2 animate-spin" />}
              Changer le mot de passe
            </Button>
          </form>
        </section>

        {/* ===== Danger zone ===== */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Trash2 size={18} className="text-red-600" />
            <h2 className="font-display text-xl font-medium tracking-tight text-red-700">Zone de danger</h2>
          </div>
          <div className="border border-red-200 bg-red-50/40 rounded-sm p-6">
            <p className="font-display text-base font-medium mb-2">Supprimer mon compte</p>
            <p className="text-sm text-zinc-700 mb-4">
              Cette action est <strong>irréversible</strong>. Votre abonnement Stripe sera résilié immédiatement et toutes vos données (workflows, exécutions, intégrations) seront supprimées définitivement.
            </p>
            <p className="text-sm text-zinc-700 mb-3">Pour confirmer, tapez <strong>SUPPRIMER</strong> dans le champ ci-dessous :</p>
            <Input
              placeholder="SUPPRIMER"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              className="rounded-sm mb-4 max-w-xs border-red-300 focus-visible:ring-red-500"
              data-testid="settings-confirm-delete-input"
            />
            <Button
              onClick={deleteAccount}
              disabled={deleting || confirmDelete !== "SUPPRIMER"}
              className="rounded-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 h-11"
              data-testid="settings-delete-account"
            >
              {deleting && <Loader2 size={14} className="mr-2 animate-spin" />}
              <Trash2 size={14} className="mr-2" />
              Supprimer mon compte définitivement
            </Button>
          </div>
        </section>
      </div>
  );
}
